import type { FinalSummaryContent } from "@/api/previewFinalSummary";

/** The summary's prose sections, in the order the Final summary dialog shows them. */
export const FINAL_SUMMARY_TEXT_SECTIONS: [
  keyof FinalSummaryContent,
  string,
][] = [
  ["chiefComplaint", "Chief complaint"],
  ["clinicalSummary", "Clinical summary"],
  ["patientHistory", "Patient history"],
  ["psychosisAssessment", "Psychosis assessment"],
  ["substanceUseAssessment", "Substance use assessment"],
  ["riskAssessment", "Risk assessment"],
  ["patientAdditions", "Patient additions"],
];

const filled = (entries: [string, string][]) =>
  entries.filter(([, value]) => value.trim() !== "");

const bullets = (entries: [string, string][]) =>
  entries.map(([key, value]) => `- **${key}**: ${value}`).join("\n");

/** The final summary as Markdown: the same filled sections the dialog shows, nothing else. */
export function finalSummaryMarkdown(content: FinalSummaryContent): string {
  const blocks = ["# Final summary"];
  for (const [key, title] of FINAL_SUMMARY_TEXT_SECTIONS) {
    const value = content[key];
    if (typeof value === "string" && value.trim() !== "") {
      blocks.push(`## ${title}\n\n${value}`);
    }
  }
  const background = filled(Object.entries(content.backgroundSections ?? {}));
  if (background.length > 0) {
    blocks.push(`## Background\n\n${bullets(background)}`);
  }
  for (const [track, items] of Object.entries(
    content.diagnosticCriteriaFindings ?? {},
  )) {
    const rows = filled(Object.entries(items));
    if (rows.length > 0) {
      blocks.push(`## Findings: ${track}\n\n${bullets(rows)}`);
    }
  }
  for (const [title, group] of [
    ["Primary diagnoses", content.indicatedDiagnoses?.primary],
    ["Secondary diagnoses", content.indicatedDiagnoses?.secondary],
  ] as const) {
    const rows = Object.entries(group ?? {}).map(
      ([track, names]): [string, string] => [track, names.join(", ")],
    );
    if (rows.length > 0) {
      blocks.push(`## ${title}\n\n${bullets(rows)}`);
    }
  }
  return `${blocks.join("\n\n")}\n`;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** reliv-labs_final-summary_<runref>_<YYYYMMDD-HHMM>.md (local time). */
export function finalSummaryFilename(runRef: string, now: Date): string {
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `reliv-labs_final-summary_${runRef.replace(/[^A-Za-z0-9_-]+/g, "")}_${stamp}.md`;
}
